/* eslint-disable react/jsx-no-bind */
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { format, parseISO, startOfMonth, endOfMonth } from 'date-fns';
import {
  Button,
  Modal,
  Divider,
  Loader,
  Tag,
  TagGroup,
} from 'rsuite';
import { toast } from 'react-toastify';
import { DarkModeContext } from '~/common/contexts/darkMode';
import { ButtonBorderBlue } from '~/components/Buttons/ButtonBorderBlue/styles';
import { ButtonFullBlue } from '~/components/Buttons/ButtonFullBlue/styles';
import SelectState from '~/components/SelectState';
import { InputDataRange } from '~/components/InputDataRange';
import api from '~/services/api';
import FiltroCidades from '../../../Mensagens/components/FiltroCidades';
import AnaliseDocumentoCard from '../AnaliseDocumentoCard';

const pickerContainer = () => document.body;

const localeDataRange = {
  sunday: 'Dom',
  monday: 'Seg',
  tuesday: 'Ter',
  wednesday: 'Qua',
  thursday: 'Qui',
  friday: 'Sex',
  saturday: 'Sab',
  ok: 'Ok',
  today: 'Hoje',
  yesterday: 'Ontem',
  hours: 'Horas',
  minutes: 'Minutos',
  seconds: 'Segundos',
};

function obterPeriodoMesAtual() {
  const hoje = new Date();
  return [startOfMonth(hoje), endOfMonth(hoje)];
}

const opcoesSimNao = [
  { label: 'Todos', value: 'todos' },
  { label: 'Sim', value: 'sim' },
  { label: 'Não', value: 'nao' },
];

const resultadoInicial = {
  resumo: {
    totalRegistros: 0,
  },
  profissionais: [],
};

const statusAnaliseDocumento = {
  approved: { texto: 'IA: aprovar', corFundo: '#ecfdf5', corTexto: '#15803d' },
  not_approved: {
    texto: 'IA: não aprovar',
    corFundo: '#fef2f2',
    corTexto: '#b91c1c',
  },
  processing: { texto: 'Processando', corFundo: '#eff6ff', corTexto: '#1d4ed8' },
  pending: { texto: 'Pendente', corFundo: '#fff7ed', corTexto: '#c2410c' },
  error: { texto: 'Erro', corFundo: '#fef2f2', corTexto: '#b91c1c' },
};

const statusAnaliseDocumentoPadrao = {
  texto: 'Sem análise',
  corFundo: '#f1f5f9',
  corTexto: '#475569',
};

function obterStatusAnaliseDocumento(analise) {
  const status = analise?.status;

  return statusAnaliseDocumento[status] || statusAnaliseDocumentoPadrao;
}

const coresSmartLiberacao = {
  azulEscuro: '#1e3a8a',
  verdePrincipal: '#16a34a',
  verdeEscuro: '#15803d',
  verdeClaro: '#dcfce7',
  verdeBorda: '#86efac',
  verdeSuave: '#f0fdf4',
  bordaSuave: '#e2e8f0',
  textoSuave: '#6b7280',
};

function formatarData(valor) {
  if (!valor) {
    return '-';
  }

  try {
    const data =
      valor instanceof Date ? valor : parseISO(String(valor));

    if (Number.isNaN(data.getTime())) {
      const fallback = new Date(valor);

      if (Number.isNaN(fallback.getTime())) {
        return '-';
      }

      return format(fallback, 'dd/MM/yyyy');
    }

    return format(data, 'dd/MM/yyyy');
  } catch {
    return '-';
  }
}

function adicionarItemFiltro(item, setState) {
  setState((estadoAnterior) => {
    if (estadoAnterior.some((filtro) => filtro.id === item.id)) {
      return estadoAnterior;
    }

    return [...estadoAnterior, item];
  });
}

function removerItemFiltro(id, setState) {
  setState((estadoAnterior) => estadoAnterior.filter((item) => item.id !== id));
}

function ImagemClicavel({ src, alt, tamanho = 56, formato = 'circle', onAbrir }) {
  const estiloBase = {
    width: tamanho,
    height: tamanho,
    borderRadius: formato === 'circle' ? '50%' : '8px',
    objectFit: 'cover',
    cursor: src ? 'pointer' : 'default',
    border: '1px solid #dbeafe',
    backgroundColor: '#f8fafc',
  };

  if (!src) {
    return (
      <div
        style={{
          ...estiloBase,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 10,
          color: '#94a3b8',
          textAlign: 'center',
          padding: 4,
        }}
      >
        Sem foto
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={(event) => {
        event.stopPropagation();
        onAbrir(src, alt);
      }}
      style={{
        padding: 0,
        border: 'none',
        background: 'transparent',
        lineHeight: 0,
      }}
      aria-label={`Ampliar ${alt}`}
    >
      <img src={src} alt={alt} style={estiloBase} />
    </button>
  );
}

ImagemClicavel.propTypes = {
  src: PropTypes.string,
  alt: PropTypes.string,
  tamanho: PropTypes.number,
  formato: PropTypes.string,
  onAbrir: PropTypes.func.isRequired,
};

ImagemClicavel.defaultProps = {
  src: null,
  alt: '',
  tamanho: 56,
  formato: 'circle',
};

const ZOOM_MIN = 1;
const ZOOM_MAX = 4;
const ZOOM_STEP = 0.12;

function CurriculoClicavel({ url, nome, tamanho = 52, onAbrir }) {
  const estiloBase = {
    width: tamanho,
    height: tamanho,
    borderRadius: '8px',
    cursor: url ? 'pointer' : 'default',
    border: '1px solid #dbeafe',
    backgroundColor: url ? '#eff6ff' : '#f8fafc',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
    gap: 2,
    padding: 4,
  };

  if (!url) {
    return (
      <div style={estiloBase}>
        <span style={{ fontSize: 9, color: '#94a3b8', textAlign: 'center' }}>
          Sem CV
        </span>
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={(event) => {
        event.stopPropagation();
        onAbrir(url, nome || 'Currículo');
      }}
      style={{
        padding: 0,
        border: 'none',
        background: 'transparent',
        lineHeight: 0,
      }}
      aria-label="Visualizar currículo"
      title={nome || 'Visualizar currículo'}
    >
      <div style={estiloBase}>
        <span
          style={{
            fontSize: 11,
            fontWeight: 800,
            color: '#1d4ed8',
            letterSpacing: '0.04em',
          }}
        >
          PDF
        </span>
        <span style={{ fontSize: 8, color: '#64748b' }}>CV</span>
      </div>
    </button>
  );
}

CurriculoClicavel.propTypes = {
  url: PropTypes.string,
  nome: PropTypes.string,
  tamanho: PropTypes.number,
  onAbrir: PropTypes.func.isRequired,
};

CurriculoClicavel.defaultProps = {
  url: null,
  nome: '',
  tamanho: 52,
};

function ImagemComZoom({ src, alt }) {
  const containerRef = useRef(null);
  const imagemRef = useRef(null);
  const escalaRef = useRef(1);
  const posicaoRef = useRef({ x: 0, y: 0 });
  const arrasteRef = useRef({
    ativo: false,
    startX: 0,
    startY: 0,
    posX: 0,
    posY: 0,
  });
  const [escala, setEscala] = useState(1);
  const [posicao, setPosicao] = useState({ x: 0, y: 0 });

  useEffect(() => {
    escalaRef.current = escala;
  }, [escala]);

  useEffect(() => {
    posicaoRef.current = posicao;
  }, [posicao]);

  useEffect(() => {
    setEscala(1);
    setPosicao({ x: 0, y: 0 });
  }, [src]);

  function resetarZoom() {
    setEscala(1);
    setPosicao({ x: 0, y: 0 });
  }

  useEffect(() => {
    const container = containerRef.current;

    if (!container) {
      return undefined;
    }

    function handleWheel(evento) {
      evento.preventDefault();

      const retangulo = container.getBoundingClientRect();
      const pontoX = evento.clientX - retangulo.left;
      const pontoY = evento.clientY - retangulo.top;

      setEscala((escalaAtual) => {
        const direcao = evento.deltaY > 0 ? -1 : 1;
        const novaEscala = Math.min(
          ZOOM_MAX,
          Math.max(ZOOM_MIN, escalaAtual + direcao * ZOOM_STEP)
        );

        if (novaEscala === escalaAtual) {
          return escalaAtual;
        }

        if (novaEscala === ZOOM_MIN) {
          setPosicao({ x: 0, y: 0 });
          return novaEscala;
        }

        const proporcao = novaEscala / escalaAtual;

        setPosicao((posicaoAtual) => ({
          x: pontoX - (pontoX - posicaoAtual.x) * proporcao,
          y: pontoY - (pontoY - posicaoAtual.y) * proporcao,
        }));

        return novaEscala;
      });
    }

    function handleMouseDown(evento) {
      if (escalaRef.current <= 1) {
        return;
      }

      arrasteRef.current = {
        ativo: true,
        startX: evento.clientX,
        startY: evento.clientY,
        posX: posicaoRef.current.x,
        posY: posicaoRef.current.y,
      };
    }

    function handleMouseMove(evento) {
      if (!arrasteRef.current.ativo) {
        return;
      }

      const deltaX = evento.clientX - arrasteRef.current.startX;
      const deltaY = evento.clientY - arrasteRef.current.startY;

      setPosicao({
        x: arrasteRef.current.posX + deltaX,
        y: arrasteRef.current.posY + deltaY,
      });
    }

    function handleMouseUp() {
      arrasteRef.current.ativo = false;
    }

    function handleDoubleClick() {
      resetarZoom();
    }

    container.addEventListener('wheel', handleWheel, { passive: false });
    container.addEventListener('mousedown', handleMouseDown);
    container.addEventListener('mousemove', handleMouseMove);
    container.addEventListener('mouseup', handleMouseUp);
    container.addEventListener('mouseleave', handleMouseUp);
    container.addEventListener('dblclick', handleDoubleClick);

    return () => {
      container.removeEventListener('wheel', handleWheel);
      container.removeEventListener('mousedown', handleMouseDown);
      container.removeEventListener('mousemove', handleMouseMove);
      container.removeEventListener('mouseup', handleMouseUp);
      container.removeEventListener('mouseleave', handleMouseUp);
      container.removeEventListener('dblclick', handleDoubleClick);
    };
  }, [src]);

  return (
    <div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 12,
          gap: 12,
          flexWrap: 'wrap',
        }}
      >
        <span style={{ fontSize: 12, color: coresSmartLiberacao.textoSuave }}>
          Use a roda do mouse para zoom. Arraste para mover. Duplo clique para
          resetar.
        </span>
        <Button
          size="xs"
          appearance="subtle"
          onClick={resetarZoom}
          disabled={escala === 1}
        >
          Resetar zoom ({Math.round(escala * 100)}%)
        </Button>
      </div>
      <div
        ref={containerRef}
        style={{
          height: '70vh',
          overflow: 'hidden',
          borderRadius: '12px',
          backgroundColor: '#0f172a',
          cursor: escala > 1 ? 'grab' : 'zoom-in',
          userSelect: 'none',
          touchAction: 'none',
          position: 'relative',
        }}
      >
        <div
          style={{
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transform: `translate(${posicao.x}px, ${posicao.y}px) scale(${escala})`,
            transformOrigin: 'center center',
          }}
        >
          <img
            ref={imagemRef}
            src={src}
            alt={alt}
            draggable={false}
            style={{
              maxWidth: '100%',
              maxHeight: '100%',
              objectFit: 'contain',
              display: 'block',
              pointerEvents: 'none',
            }}
          />
        </div>
      </div>
    </div>
  );
}

ImagemComZoom.propTypes = {
  src: PropTypes.string.isRequired,
  alt: PropTypes.string,
};

ImagemComZoom.defaultProps = {
  alt: '',
};

function SmartLiberacao({ onConcluido }) {
  const { toggleMode } = useContext(DarkModeContext);
  const [exibir, setExibir] = useState(false);
  const [carregando, setCarregando] = useState(false);
  const [executando, setExecutando] = useState(false);
  const [simulou, setSimulou] = useState(false);
  const [cpfValido, setCpfValido] = useState('todos');
  const [curriculoAdicionado, setCurriculoAdicionado] = useState('todos');
  const [certificadoAdicionado, setCertificadoAdicionado] = useState('todos');
  const [experienciaAdicionada, setExperienciaAdicionada] = useState('todos');
  const [temDocumento, setTemDocumento] = useState('todos');
  const [maiorDe18Anos, setMaiorDe18Anos] = useState('todos');
  const [jaTrabalhouAlgumaVez, setJaTrabalhouAlgumaVez] = useState('todos');
  const [periodoCadastro, setPeriodoCadastro] = useState(obterPeriodoMesAtual);
  const [estadoSelecionado, setEstadoSelecionado] = useState();
  const [cidadesSelecionadas, setCidadesSelecionadas] = useState([]);
  const [resultado, setResultado] = useState(resultadoInicial);
  const [profissionaisSelecionados, setProfissionaisSelecionados] = useState([]);
  const [lightboxAberto, setLightboxAberto] = useState(false);
  const [lightboxImagem, setLightboxImagem] = useState('');
  const [lightboxTitulo, setLightboxTitulo] = useState('');
  const [lightboxTipo, setLightboxTipo] = useState('imagem');
  const [profissionalAnalise, setProfissionalAnalise] = useState(null);

  const resumo = useMemo(() => resultado.resumo || {}, [resultado]);
  const selecionadosSet = useMemo(
    () => new Set(profissionaisSelecionados),
    [profissionaisSelecionados]
  );
  const totalSelecionados = profissionaisSelecionados.length;

  function abrirLightbox(imagem, titulo, tipo = 'imagem') {
    if (!imagem) {
      return;
    }

    setLightboxImagem(imagem);
    setLightboxTitulo(titulo);
    setLightboxTipo(tipo);
    setLightboxAberto(true);
  }

  function fecharLightbox() {
    setLightboxAberto(false);
    setLightboxImagem('');
    setLightboxTitulo('');
    setLightboxTipo('imagem');
  }

  function abrirAnaliseDocumento(profissional) {
    setProfissionalAnalise(profissional);
  }

  function fecharAnaliseDocumento() {
    setProfissionalAnalise(null);
  }

  function atualizarAnaliseProfissional(idProfissional, analise) {
    setResultado((estadoAnterior) => ({
      ...estadoAnterior,
      profissionais: (estadoAnterior.profissionais || []).map((item) =>
        item.id === idProfissional
          ? { ...item, analise_documento: analise || null }
          : item
      ),
    }));

    setProfissionalAnalise((estadoAnterior) => {
      if (!estadoAnterior || estadoAnterior.id !== idProfissional) {
        return estadoAnterior;
      }

      return {
        ...estadoAnterior,
        analise_documento: analise || null,
      };
    });
  }

  function abrirModal() {
    setExibir(true);
  }

  function resetarEstado() {
    setExibir(false);
    setCarregando(false);
    setExecutando(false);
    setSimulou(false);
    setCpfValido('todos');
    setCurriculoAdicionado('todos');
    setCertificadoAdicionado('todos');
    setExperienciaAdicionada('todos');
    setTemDocumento('todos');
    setMaiorDe18Anos('todos');
    setJaTrabalhouAlgumaVez('todos');
    setPeriodoCadastro(obterPeriodoMesAtual());
    setEstadoSelecionado(undefined);
    setCidadesSelecionadas([]);
    setResultado(resultadoInicial);
    setProfissionaisSelecionados([]);
    fecharLightbox();
    fecharAnaliseDocumento();
  }

  function definirSelecaoInicial(profissionais = []) {
    setProfissionaisSelecionados(profissionais.map((item) => item.id));
  }

  function alternarProfissionalSelecionado(idProfissional, selecionado) {
    setProfissionaisSelecionados((estadoAnterior) => {
      if (selecionado) {
        if (estadoAnterior.includes(idProfissional)) {
          return estadoAnterior;
        }

        return [...estadoAnterior, idProfissional];
      }

      return estadoAnterior.filter((item) => item !== idProfissional);
    });
  }

  function renderFiltroSegmentado(label, value, onChange) {
    return (
      <div>
        <span>{label}</span>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
            gap: '6px',
          }}
        >
          {opcoesSimNao.map((opcao) => {
            const ativo = value === opcao.value;
            let backgroundColor = ativo
              ? coresSmartLiberacao.verdePrincipal
              : undefined;
            let color = ativo ? '#fff' : undefined;

            if (!ativo && toggleMode) {
              backgroundColor = '#111827';
              color = '#dbeafe';
            }

            if (!ativo && !toggleMode) {
              backgroundColor = '#fff';
              color = coresSmartLiberacao.azulEscuro;
            }

            return (
              <button
                key={opcao.value}
                type="button"
                onClick={() => onChange(opcao.value)}
                style={{
                  minHeight: 34,
                  borderRadius: 10,
                  border: `1px solid ${
                    ativo
                      ? coresSmartLiberacao.verdePrincipal
                      : coresSmartLiberacao.bordaSuave
                  }`,
                  backgroundColor,
                  color,
                  fontSize: 12,
                  fontWeight: 700,
                  cursor: 'pointer',
                }}
              >
                {opcao.label}
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  async function carregarSimulacao() {
    setCarregando(true);

    try {
      const response = await api.post(
        'profissionais/bloqueados/smart-liberacao/simular',
        {
          cpfValido,
          curriculoAdicionado,
          certificadoAdicionado,
          experienciaAdicionada,
          temDocumento,
          maiorDe18Anos,
          jaTrabalhouAlgumaVez,
          estado: estadoSelecionado ? Number(estadoSelecionado) : null,
          cidades: cidadesSelecionadas.map((item) => item.id),
          dataInicio:
            Array.isArray(periodoCadastro) && periodoCadastro[0]
              ? format(new Date(periodoCadastro[0]), 'yyyy-MM-dd')
              : null,
          dataFim:
            Array.isArray(periodoCadastro) && periodoCadastro[1]
              ? format(new Date(periodoCadastro[1]), 'yyyy-MM-dd')
              : null,
        }
      );

      setResultado(response.data);
      definirSelecaoInicial(response.data.profissionais || []);
      setSimulou(true);
      return response.data;
    } catch (error) {
      toast.error(error.message);
      return null;
    } finally {
      setCarregando(false);
    }
  }

  async function simularLiberacao() {
    await carregarSimulacao();
  }

  async function liberarConfirmacoes() {
    if (profissionaisSelecionados.length === 0) {
      toast.error('Selecione ao menos um profissional para liberar.');
      return;
    }

    setExecutando(true);

    try {
      const response = await api.post(
        'profissionais/bloqueados/smart-liberacao/liberar',
        {
          ids: profissionaisSelecionados,
        }
      );
      const { resumo: resumoExecucao, falhas = [] } = response.data;

      if (resumoExecucao.sucesso > 0) {
        toast.success(
          `${resumoExecucao.sucesso} profissional(is) liberado(s) com sucesso.`
        );

        if (onConcluido) {
          await onConcluido();
        }

        await carregarSimulacao();
      }

      if (falhas.length > 0) {
        const mensagemFalhas = falhas
          .slice(0, 3)
          .map((item) => `Profissional #${item.id}: ${item.erro}`)
          .join(' | ');

        toast.error(mensagemFalhas);
      }
    } catch (error) {
      toast.error(error.message);
    }

    setExecutando(false);
  }

  return (
    <>
      <style>
        {`
          .smart-liberacao-modal .rs-checkbox-checked .rs-checkbox-checker label::before {
            background-color: ${coresSmartLiberacao.verdePrincipal};
            border-color: ${coresSmartLiberacao.verdePrincipal};
          }

          .smart-liberacao-modal .rs-picker-toggle.rs-btn:hover,
          .smart-liberacao-modal .rs-picker-toggle.rs-btn:focus,
          .smart-liberacao-modal .rs-picker-focused .rs-picker-toggle {
            border-color: ${coresSmartLiberacao.verdeBorda};
          }

          .smart-liberacao-modal .rs-input:focus,
          .smart-liberacao-modal .rs-input:hover,
          .smart-liberacao-modal .rs-picker-toggle-wrapper:hover,
          .smart-liberacao-modal .rs-picker-toggle-wrapper:focus-within {
            border-color: ${coresSmartLiberacao.verdeBorda};
          }

          .smart-liberacao-modal .smart-link-filtros > div {
            margin-bottom: 14px !important;
            padding: 0 0 14px;
            border: none;
            border-bottom: 1px solid ${
              toggleMode ? '#243245' : coresSmartLiberacao.bordaSuave
            };
            background: transparent;
            box-shadow: none;
          }

          .smart-liberacao-modal .smart-link-filtros > div:last-child {
            margin-bottom: 0 !important;
            padding-bottom: 0;
            border-bottom: none;
          }

          .smart-liberacao-modal .smart-link-filtros > div > span {
            display: block;
            margin-bottom: 8px;
            font-size: 11px;
            font-weight: 700;
            letter-spacing: 0.05em;
            text-transform: uppercase;
            color: ${toggleMode ? '#bfdbfe' : coresSmartLiberacao.azulEscuro};
          }

          .smart-liberacao-modal .smart-link-filtros .rs-picker-toggle,
          .smart-liberacao-modal .smart-link-filtros .rs-input,
          .smart-liberacao-modal .smart-link-filtros .rs-input-group {
            min-height: 36px;
            border-radius: 10px;
            border-color: ${toggleMode ? '#243245' : '#dbeafe'};
            background: ${toggleMode ? '#0f172a' : '#f8fafc'};
            box-shadow: none;
          }

          .smart-liberacao-imagem-modal,
          .smart-liberacao-analise-modal {
            z-index: 2000 !important;
          }

          .smart-liberacao-analise-modal .rs-modal {
            width: 92vw !important;
            max-width: 1180px;
          }

          .smart-liberacao-analise-modal .rs-panel {
            margin-bottom: 0;
          }
        `}
      </style>

      <ButtonBorderBlue
        appearance="ghost"
        darkMode={toggleMode}
        onClick={abrirModal}
        style={{
          borderColor: coresSmartLiberacao.verdePrincipal,
          color: coresSmartLiberacao.verdePrincipal,
        }}
      >
        Smart Liberação
      </ButtonBorderBlue>

      <Modal
        open={exibir}
        onClose={resetarEstado}
        size="full"
        className="smart-liberacao-modal"
      >
        <Modal.Header>
          <Modal.Title>
            <div>
              <h4 style={{ margin: 0 }}>Smart Liberação</h4>
              <span
                style={{
                  fontSize: 13,
                  color: coresSmartLiberacao.textoSuave,
                }}
              >
                Simule e libere profissionais bloqueados a partir dos filtros
                informados.
              </span>
            </div>
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '320px 1fr',
              gap: '24px',
              minHeight: '70vh',
            }}
          >
            <div
              className="smart-link-filtros"
              style={{
                border: `1px solid ${toggleMode ? '#243245' : '#dbeafe'}`,
                borderRadius: '18px',
                padding: '18px',
                backgroundColor: toggleMode ? '#1c1c24' : '#ffffff',
                boxShadow: toggleMode
                  ? 'none'
                  : '0 10px 28px rgba(29, 78, 216, 0.06)',
              }}
            >
              <h5
                style={{
                  marginTop: 0,
                  marginBottom: 16,
                  fontSize: 13,
                  letterSpacing: '0.05em',
                  textTransform: 'uppercase',
                  color: toggleMode
                    ? '#bfdbfe'
                    : coresSmartLiberacao.azulEscuro,
                }}
              >
                Filtros de busca
              </h5>

              <div style={{ marginBottom: 20 }}>
                <span style={{ display: 'block', marginBottom: 8 }}>
                  Data de cadastro
                </span>
                <InputDataRange
                  ranges={[]}
                  isoWeek
                  value={periodoCadastro}
                  locale={localeDataRange}
                  onChange={setPeriodoCadastro}
                />
              </div>

              <div style={{ marginBottom: 20 }}>
                <span style={{ display: 'block', marginBottom: 8 }}>Estado</span>
                <SelectState
                  selectedState={estadoSelecionado}
                  setSelectedStates={setEstadoSelecionado}
                  searchable
                  container={pickerContainer}
                  style={{ width: '100%' }}
                />
              </div>

              <div style={{ marginBottom: 20 }}>
                <span style={{ display: 'block', marginBottom: 8 }}>Cidades</span>
                {cidadesSelecionadas.length > 0 ? (
                  <TagGroup style={{ marginBottom: 10 }}>
                    {cidadesSelecionadas.map((cidade) => (
                      <Tag
                        key={cidade.id}
                        closable
                        style={{
                          backgroundColor: coresSmartLiberacao.verdeClaro,
                          border: `1px solid ${coresSmartLiberacao.verdeBorda}`,
                          color: coresSmartLiberacao.verdeEscuro,
                        }}
                        onClose={() =>
                          removerItemFiltro(cidade.id, setCidadesSelecionadas)
                        }
                      >
                        Cidade: {cidade.label}
                      </Tag>
                    ))}
                  </TagGroup>
                ) : null}
                <FiltroCidades
                  disabled={carregando || executando}
                  onItemAdicionado={(value, item) => {
                    if (!item || item.id == null) {
                      return;
                    }

                    adicionarItemFiltro(item, setCidadesSelecionadas);
                  }}
                />
              </div>

              {renderFiltroSegmentado(
                'CPF válido',
                cpfValido,
                setCpfValido
              )}

              {renderFiltroSegmentado(
                'Currículo adicionado',
                curriculoAdicionado,
                setCurriculoAdicionado
              )}

              {renderFiltroSegmentado(
                'Certificado adicionado',
                certificadoAdicionado,
                setCertificadoAdicionado
              )}

              {renderFiltroSegmentado(
                'Experiência adicionada',
                experienciaAdicionada,
                setExperienciaAdicionada
              )}

              {renderFiltroSegmentado(
                'Validar se tem documento',
                temDocumento,
                setTemDocumento
              )}

              {renderFiltroSegmentado(
                'Maior de 18 anos',
                maiorDe18Anos,
                setMaiorDe18Anos
              )}

              {renderFiltroSegmentado(
                'Já trabalhou alguma vez',
                jaTrabalhouAlgumaVez,
                setJaTrabalhouAlgumaVez
              )}
            </div>

            <div
              style={{
                border: `1px solid ${
                  toggleMode ? '#294235' : coresSmartLiberacao.verdeBorda
                }`,
                borderRadius: '12px',
                padding: '20px',
                backgroundColor: toggleMode ? '#1c1c24' : '#fff',
                display: 'flex',
                flexDirection: 'column',
                boxShadow: toggleMode
                  ? 'none'
                  : '0 8px 24px rgba(22, 163, 74, 0.08)',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  gap: '16px',
                }}
              >
                <div>
                  <h5 style={{ marginTop: 0, marginBottom: 4 }}>
                    Profissionais bloqueados
                  </h5>
                  <span
                    style={{
                      fontSize: 13,
                      color: coresSmartLiberacao.textoSuave,
                    }}
                  >
                    Exibe os profissionais bloqueados que atendem aos filtros.
                    Os resultados vêm pré-selecionados para liberação.
                  </span>
                </div>

                {simulou ? (
                  <div style={{ display: 'flex', gap: '16px', fontSize: 13 }}>
                    <strong>{resumo.totalRegistros || 0} encontrados</strong>
                    <strong>{totalSelecionados} selecionados</strong>
                  </div>
                ) : null}
              </div>

              <Divider style={{ margin: '16px 0' }} />

              {carregando ? (
                <div
                  style={{
                    flex: 1,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  <Loader
                    size="md"
                    content="Simulando liberação..."
                    vertical
                  />
                </div>
              ) : null}

              {!carregando && !simulou ? (
                <div
                  style={{
                    flex: 1,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    color: coresSmartLiberacao.textoSuave,
                    textAlign: 'center',
                    padding: '0 40px',
                  }}
                >
                  Preencha os filtros e clique em{' '}
                  <strong style={{ marginLeft: 4 }}>Simular Liberação</strong>.
                </div>
              ) : null}

              {!carregando &&
              simulou &&
              resultado.profissionais.length === 0 ? (
                <div
                  style={{
                    flex: 1,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    color: coresSmartLiberacao.textoSuave,
                    textAlign: 'center',
                    padding: '0 40px',
                  }}
                >
                  Nenhum profissional bloqueado atendeu aos filtros informados.
                </div>
              ) : null}

              {!carregando && resultado.profissionais.length > 0 ? (
                <div
                  style={{ flex: 1, overflowY: 'auto', paddingRight: '8px' }}
                >
                  {resultado.profissionais.map((profissional) => {
                    const statusAnalise = obterStatusAnaliseDocumento(
                      profissional.analise_documento
                    );

                    return (
                    <div
                      key={profissional.id}
                      style={{
                        border: `1px solid ${
                          toggleMode ? '#294235' : '#dbeafe'
                        }`,
                        borderRadius: '18px',
                        padding: '18px 22px',
                        marginBottom: '14px',
                        backgroundColor: toggleMode ? '#15151c' : '#fff',
                        boxShadow: toggleMode
                          ? 'none'
                          : '0 10px 24px rgba(15, 23, 42, 0.06)',
                      }}
                    >
                      <div
                        style={{
                          display: 'grid',
                          gridTemplateColumns:
                            '34px 176px minmax(220px, 1fr) repeat(3, minmax(100px, 0.7fr)) minmax(132px, 0.8fr)',
                          gap: '16px',
                          alignItems: 'center',
                        }}
                      >
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            cursor: executando ? 'not-allowed' : 'pointer',
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={selecionadosSet.has(profissional.id)}
                            disabled={executando}
                            onChange={(event) =>
                              alternarProfissionalSelecionado(
                                profissional.id,
                                event.target.checked
                              )
                            }
                            style={{
                              width: 16,
                              height: 16,
                              accentColor: coresSmartLiberacao.verdePrincipal,
                              cursor: executando ? 'not-allowed' : 'pointer',
                            }}
                          />
                        </div>

                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                          }}
                        >
                          <ImagemClicavel
                            src={profissional.foto_perfil}
                            alt={`Foto de ${profissional.nome}`}
                            tamanho={52}
                            formato="circle"
                            onAbrir={(url, titulo) =>
                              abrirLightbox(url, titulo, 'imagem')
                            }
                          />
                          <ImagemClicavel
                            src={profissional.documento}
                            alt={`Documento de ${profissional.nome}`}
                            tamanho={52}
                            formato="rounded"
                            onAbrir={(url, titulo) =>
                              abrirLightbox(url, titulo, 'imagem')
                            }
                          />
                          <CurriculoClicavel
                            url={profissional.curriculo}
                            nome={profissional.curriculo_nome}
                            tamanho={52}
                            onAbrir={(url, titulo) =>
                              abrirLightbox(url, titulo, 'pdf')
                            }
                          />
                        </div>

                        <div style={{ minWidth: 0 }}>
                          <div
                            style={{
                              fontSize: 18,
                              fontWeight: 700,
                              color: toggleMode ? '#f9fafb' : '#0f172a',
                              lineHeight: 1.15,
                            }}
                          >
                            {profissional.nome}
                          </div>
                          <div
                            style={{
                              marginTop: 4,
                              fontSize: 13,
                              color: coresSmartLiberacao.textoSuave,
                            }}
                          >
                            {profissional.email}
                          </div>
                          <div
                            style={{
                              marginTop: 6,
                              display: 'flex',
                              gap: '6px',
                              flexWrap: 'wrap',
                            }}
                          >
                            <span
                              style={{
                                padding: '3px 8px',
                                borderRadius: '999px',
                                backgroundColor: profissional.cpf_valido
                                  ? '#ecfdf5'
                                  : '#fef2f2',
                                color: profissional.cpf_valido
                                  ? coresSmartLiberacao.verdeEscuro
                                  : '#b91c1c',
                                fontSize: 11,
                                fontWeight: 600,
                              }}
                            >
                              CPF {profissional.cpf_valido ? 'válido' : 'inválido'}
                            </span>
                            {profissional.flags?.tem_documento ? (
                              <span
                                style={{
                                  padding: '3px 8px',
                                  borderRadius: '999px',
                                  backgroundColor: '#eff6ff',
                                  color: '#1d4ed8',
                                  fontSize: 11,
                                  fontWeight: 600,
                                }}
                              >
                                Com documento
                              </span>
                            ) : null}
                            {profissional.flags?.tem_curriculo ? (
                              <span
                                style={{
                                  padding: '3px 8px',
                                  borderRadius: '999px',
                                  backgroundColor: '#f0fdf4',
                                  color: coresSmartLiberacao.verdeEscuro,
                                  fontSize: 11,
                                  fontWeight: 600,
                                }}
                              >
                                Com currículo
                              </span>
                            ) : null}
                          </div>
                        </div>

                        <div>
                          <div
                            style={{
                              fontSize: 11,
                              textTransform: 'uppercase',
                              color: coresSmartLiberacao.textoSuave,
                              marginBottom: 4,
                            }}
                          >
                            CPF/CNPJ
                          </div>
                          <div style={{ fontWeight: 600 }}>
                            {profissional.cpf_cnpj || '-'}
                          </div>
                        </div>

                        <div>
                          <div
                            style={{
                              fontSize: 11,
                              textTransform: 'uppercase',
                              color: coresSmartLiberacao.textoSuave,
                              marginBottom: 4,
                            }}
                          >
                            Cidade/UF
                          </div>
                          <div style={{ fontWeight: 600 }}>
                            {profissional.cidade || '-'}
                            {profissional.estado ? ` / ${profissional.estado}` : ''}
                          </div>
                        </div>

                        <div>
                          <div
                            style={{
                              fontSize: 11,
                              textTransform: 'uppercase',
                              color: coresSmartLiberacao.textoSuave,
                              marginBottom: 4,
                            }}
                          >
                            Cadastro
                          </div>
                          <div style={{ fontWeight: 600 }}>
                            {formatarData(profissional.data_criacao)}
                          </div>
                        </div>

                        <div>
                          <div
                            style={{
                              fontSize: 11,
                              textTransform: 'uppercase',
                              color: coresSmartLiberacao.textoSuave,
                              marginBottom: 4,
                            }}
                          >
                            Análise IA
                          </div>
                          <button
                            type="button"
                            aria-label={`Abrir análise de documento de ${profissional.nome}`}
                            onClick={(event) => {
                              event.stopPropagation();
                              abrirAnaliseDocumento(profissional);
                            }}
                            style={{
                              padding: '4px 10px',
                              borderRadius: '999px',
                              border: 'none',
                              cursor: 'pointer',
                              fontSize: 11,
                              fontWeight: 700,
                              backgroundColor: statusAnalise.corFundo,
                              color: statusAnalise.corTexto,
                            }}
                          >
                            {statusAnalise.texto}
                          </button>
                        </div>
                      </div>
                    </div>
                    );
                  })}
                </div>
              ) : null}
            </div>
          </div>
        </Modal.Body>

        <Modal.Footer>
          <Button onClick={resetarEstado} appearance="subtle">
            Fechar
          </Button>
          <ButtonFullBlue
            appearance="primary"
            onClick={simularLiberacao}
            disabled={carregando || executando}
            style={{
              backgroundColor: coresSmartLiberacao.verdeSuave,
              borderColor: coresSmartLiberacao.verdeBorda,
              color: coresSmartLiberacao.verdeEscuro,
            }}
          >
            Simular Liberação
          </ButtonFullBlue>
          <Button
            appearance="primary"
            onClick={liberarConfirmacoes}
            disabled={
              !simulou || totalSelecionados === 0 || carregando || executando
            }
            style={{
              backgroundColor: coresSmartLiberacao.verdePrincipal,
              borderColor: coresSmartLiberacao.verdePrincipal,
            }}
          >
            {executando ? 'Liberando...' : 'Liberar Confirmações'}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        open={Boolean(profissionalAnalise)}
        onClose={fecharAnaliseDocumento}
        size="lg"
        overflow
        className="smart-liberacao-analise-modal"
      >
        <Modal.Header>
          Análise de documento
          {profissionalAnalise ? ` — ${profissionalAnalise.nome}` : ''}
        </Modal.Header>
        <Modal.Body>
          {profissionalAnalise ? (
            <AnaliseDocumentoCard
              idProfissional={profissionalAnalise.id}
              analise={profissionalAnalise.analise_documento}
              documento={{
                caminho: profissionalAnalise.documento,
                arquivo: profissionalAnalise.documento_nome,
              }}
              onAnaliseAtualizada={(analise) =>
                atualizarAnaliseProfissional(profissionalAnalise.id, analise)
              }
            />
          ) : null}
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={fecharAnaliseDocumento} appearance="primary">
            Fechar
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        open={lightboxAberto}
        onClose={fecharLightbox}
        size="lg"
        overflow
        className="smart-liberacao-imagem-modal"
      >
        <Modal.Header>
          {lightboxTitulo ||
            (lightboxTipo === 'pdf' ? 'Visualizar currículo' : 'Visualizar imagem')}
        </Modal.Header>
        <Modal.Body style={{ padding: lightboxTipo === 'pdf' ? '16px' : '24px' }}>
          {lightboxImagem && lightboxTipo === 'imagem' ? (
            <ImagemComZoom
              src={lightboxImagem}
              alt={lightboxTitulo || 'Imagem ampliada'}
            />
          ) : null}

          {lightboxImagem && lightboxTipo === 'pdf' ? (
            <iframe
              title={lightboxTitulo || 'Currículo'}
              src={lightboxImagem}
              style={{
                width: '100%',
                height: '75vh',
                border: 'none',
                borderRadius: '12px',
                backgroundColor: '#f8fafc',
              }}
            />
          ) : null}
        </Modal.Body>
        <Modal.Footer>
          {lightboxTipo === 'pdf' && lightboxImagem ? (
            <Button
              appearance="ghost"
              onClick={() => window.open(lightboxImagem, '_blank')}
            >
              Abrir em nova aba
            </Button>
          ) : null}
          <Button onClick={fecharLightbox} appearance="primary">
            Fechar
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

SmartLiberacao.propTypes = {
  onConcluido: PropTypes.func,
};

SmartLiberacao.defaultProps = {
  onConcluido: undefined,
};

export default SmartLiberacao;
